import {StyledTargetDeletionNameText} from '@/components/commons/delete-dialog/style'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {formatLocaleDate} from '@/dayjs'
import {ProjectReopeningFormValidationSchema} from '@/features/projects/formSchemas'
import {useStoreRenewTransformProject} from '@/features/projects/queries/useStoreRenewTransformProject'
import {ProjectExtended} from '@/features/projects/types'
import {assistiveRegimeToRehabCareCommitment} from '@/features/projects/utils'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {raise} from '@/utilities/helpers'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {useFormContext} from 'react-hook-form'
import {generatePath, useNavigate} from 'react-router-dom'

export const ProjectReopeningConfirmationModal = ({
    project,
    dialogStore
}: {
    project: ProjectExtended
    dialogStore: DialogStore
}) => {
    const {t} = useTranslation()
    const form = useFormContext<ProjectReopeningFormValidationSchema>()
    const navigate = useNavigate()
    const endDate = project.endDate || project.expirationDate

    const {mutate: editProjectDetails, isPending} = useStoreRenewTransformProject({
        projectId: project.id,
        options: {
            onSuccess: data => {
                dialogStore.setOpen(false)
                queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_AVAILABILITY]})
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS, project.treatmentRequestId]})
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, project.patientId]})
                toast.success(
                    t('projects:reopen_project_success', {
                        code: project.code,
                        newProjectCode: data.code
                    })
                )
                navigate(generatePath(routes.PROJECT_DETAILS_PROJECT_DATA.handle.path, {id: data.id}), {replace: true})
            },
            onError: () => {
                toast.error(t('errors:default'))
            }
        }
    })

    const handleConfirmSubmit = form.handleSubmit(
        formValues => {
            editProjectDetails({
                action: 'variation',
                empty: formValues.siarProject == 'empty',
                type: project.type || raise('Project type is required'),
                sector: formValues.sector,
                assistiveRegime: formValues.assistiveRegime || null,
                startDate: formatLocaleDate(formValues.startDate, 'YYYY-MM-DD'),
                expirationDate: formatLocaleDate(formValues.expirationDate, 'YYYY-MM-DD'),
                durationDays: formValues.durationDays || dayjs(formValues.startDate).diff(project.endDate, 'day'),
                sender: formValues.sender,
                specialistId: formValues.specialist.id,
                // This is sent to the backend just to allow the SIAR export, it is not a field of our form
                rehabilitativeAssistanceCommitment:
                    formValues.sector == 'outpatient' && formValues.assistiveRegime
                        ? assistiveRegimeToRehabCareCommitment[formValues.assistiveRegime].toString()
                        : undefined
            })
        },
        error => {
            console.error(error)
        }
    )

    return (
        <ConfirmationDialog
            dialogStore={dialogStore}
            onConfirm={handleConfirmSubmit}
            isConfirmPending={isPending}
            headerSlot={
                <Flexbox direction="column">
                    <DialogHeading>{t('projects:reopen_project')}</DialogHeading>
                    <Flexbox direction="column" gap={1}>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:reopen_project_confirmation')}
                        </Text>
                        <StyledTargetDeletionNameText palette="neutral" shade="500" size="textSm">
                            {t('projects:code_date_closed', {
                                code: project.code,
                                date: dayjs(endDate).format('DD/MM/YYYY')
                            })}
                        </StyledTargetDeletionNameText>
                    </Flexbox>
                </Flexbox>
            }
        />
    )
}
